/**
 * Device fingerprinting.
 *
 * A fingerprint is a SHA-256 digest of stable browser/hardware signals. It is
 * deterministic for a given device+browser, which is what lets a super-admin
 * approve a device once and have it recognised on every later sign-in.
 * Signals are injectable so the logic is testable outside a browser.
 */

export interface DeviceSignals {
  userAgent: string;
  platform: string;
  language: string;
  timeZone: string;
  /** `widthxheight@dpr` */
  screen: string;
  hardwareConcurrency: number;
}

const UNKNOWN_SIGNALS: DeviceSignals = {
  userAgent: 'unknown',
  platform: 'unknown',
  language: 'he-IL',
  timeZone: 'Asia/Jerusalem',
  screen: '0x0@1',
  hardwareConcurrency: 0,
};

/** Reads the signals from the current browser (falls back to constants). */
export function collectDeviceSignals(): DeviceSignals {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return { ...UNKNOWN_SIGNALS };

  let timeZone = UNKNOWN_SIGNALS.timeZone;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || timeZone;
  } catch {
    /* keep the fallback */
  }

  return {
    userAgent: navigator.userAgent ?? UNKNOWN_SIGNALS.userAgent,
    platform: (navigator as Navigator & { platform?: string }).platform ?? UNKNOWN_SIGNALS.platform,
    language: navigator.language ?? UNKNOWN_SIGNALS.language,
    timeZone,
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}@${window.devicePixelRatio ?? 1}`,
    hardwareConcurrency: navigator.hardwareConcurrency ?? 0,
  };
}

/** Canonical serialisation — key order must never change the digest. */
function canonicalize(signals: DeviceSignals): string {
  return [
    signals.userAgent,
    signals.platform,
    signals.language,
    signals.timeZone,
    signals.screen,
    String(signals.hardwareConcurrency),
  ].join('|');
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** 64-char hex SHA-256 digest of the device signals. */
export async function computeDeviceFingerprint(
  signals: DeviceSignals = collectDeviceSignals(),
): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalize(signals));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return toHex(digest);
}

/** Short form for display: `a1b2c3d4…e5f6`. */
export function shortFingerprint(fingerprint: string): string {
  if (fingerprint.length <= 16) return fingerprint;
  return `${fingerprint.slice(0, 8)}…${fingerprint.slice(-4)}`;
}

const BROWSERS: ReadonlyArray<[RegExp, string]> = [
  [/edg\//i, 'Edge'],
  [/opr\/|opera/i, 'Opera'],
  [/chrome|crios/i, 'Chrome'],
  [/firefox|fxios/i, 'Firefox'],
  [/safari/i, 'Safari'],
];

const PLATFORMS: ReadonlyArray<[RegExp, string]> = [
  [/windows/i, 'Windows'],
  [/android/i, 'Android'],
  [/iphone|ipad|ios/i, 'iOS'],
  [/mac/i, 'macOS'],
  [/linux/i, 'Linux'],
];

/** Human readable device label, e.g. `Chrome · Windows`. */
export function describeDevice(signals: DeviceSignals = collectDeviceSignals()): string {
  const ua = signals.userAgent ?? '';
  const browser = BROWSERS.find(([re]) => re.test(ua))?.[1] ?? 'דפדפן';
  const platform =
    PLATFORMS.find(([re]) => re.test(ua) || re.test(signals.platform ?? ''))?.[1] ?? 'מכשיר';
  return `${browser} · ${platform}`;
}
